// transactionConfig — transactions entity, built ENTIRELY on the generated
// presentation layer (Layer 3, docs/plans/schema-codegen-architecture.md).
//
// `transactionPresentation` is the SINGLE field source for this entity.
// Every other entity that previously imported `transactionSchema` now imports
// from here via `transactionPresentation` (column display) or
// `transactionDataSchema` (sub-table query schema).
//
// Fields that use subQuery + render escapes (load_invoice, unload_invoice,
// transport_invoice, unload_confirmation, logistics_emails, last_reminder_sent,
// send_reminder) cannot be expressed as PresentationFields — their bodies are
// kept as standalone field constants below and wired into the view section
// `layout.fields` directly.

import React from "react";
import type { EntityConfig } from "@ds/config/entityConfig";
import type { ListConfig } from "@ds/config/pageConfig";
import type { SectionDef, DataSection } from "@ds/config/sectionConfig";
import type { ListColumnSpec } from "@ds/tables/types";
import type { Transaction, TransactionFile, Transport } from "@shared/types/transaction";
import type { EmailSend } from "@shared/types/employee";
import type { ResolvedLinkWithInvoice } from "@shared/types/invoice";
import {
  buildEntityConfig,
  type ViewChrome,
  type RecordSectionSpec,
} from "@ds/config/buildEntityConfig";
import { definePresentation } from "@ds/config/presentation";
import { definePresentationConfig } from "@ds/config/presentationConfig";
import { asDataSchema, toListColumn } from "@ds/config/presentationRefs";
import { db } from "@ds/config/db/graph";
import { transactionFilePresentation } from "./transactionFileSchema";
import {
  emailSendPresentation,
  transportPresentation,
  transactionResolvedLinkDataSchema,
} from "./transactionSubresourceSchemas";
import { companyParty, productChip, employeeChip } from "@ds/config/db/refs";
import { EntityLink, type EntityLinkProps } from "@ds/connected/EntityLink";
import { DateValue } from "@ds/display/DateValue";
import { TextValue } from "@ds/display/TextValue";
import { EmptyValue } from "@ds/display/EmptyValue";
import { AmountDisplay } from "@ds/display/AmountDisplay";
import { WeightValue } from "@ds/display/WeightValue";
import { EnumChip, type EnumChipProps } from "@ds/domain/EnumChip";
import { DprChip, type DprChipProps } from "@ds/domain/DprChip";
import { YesNoChip, type YesNoChipProps } from "@ds/domain/YesNoChip";
import { RecyclingProcessChip } from "@ds/domain/RecyclingProcessChip";
import { TransactionStatusTag, InvoicedStatusTag } from "@ds/domain/EnumTag";
import { TransactionRoute } from "@ds/domain/TransactionRoute";
import type { RenderContext } from "@ds/display/fieldDisplay";

import {
  activitySection,
  commentsSection,
} from "@ds/connected/sharedSections";
import { defineAuditChildScope } from "@ds/data/auditChildScope";

import {
  PIPELINE_QUICK_FILTERS,
  PIPELINE_ADVANCED_FILTERS,
} from "./transactionsFilters";
import { transactionDocumentsSection } from "./sections/TransactionDocuments.section";
import type { TransactionActionsContextValue } from "./hooks/TransactionActionsContext";
// Bespoke React sections — each one's "stay-custom" justification is the same
// as in the legacy config: they need React beyond a declarative FieldDef[].
import { TransactionParties }       from "./sections/TransactionParties";
import { TransactionFaktury }       from "./sections/TransactionFaktury";
import { TransactionTransport }     from "./sections/TransactionTransport";
import { TransactionPricing }       from "./sections/TransactionPricing";
import { TransactionChecklist }     from "./sections/TransactionChecklist";
import { TransactionMeta }          from "./sections/TransactionMeta";
import { TransactionStatusPills }   from "./sections/TransactionStatusPills";
import { TransactionKpiBand }       from "./sections/TransactionKpiBand";
import { useTransactionWorkflowOptional } from "./hooks/transactionWorkflowContext";
import {
  UnloadConfirmationInlineCell, LogisticsEmailsInlineCell,
  LastReminderInlineCell, SendReminderInlineCell,
} from "./transactionEmailCells";
import { LOGISTICS_EMAIL_DEFS } from "./transactionEmailDefs";
import { useEntityFieldEdit } from "@ds/actions/useEntityFieldEdit";
import { AssignCell } from "@ds/tables/cells/AssignCell";
import { TruckOutlined } from "@ant-design/icons";
import { useSubQuery } from "@ds/data/subQueriesContext";
import { getSchemaMeta } from "@ds/config/schema";
import {
  WasteCodeCell, PickupDateRangeCell, TransitEditCell,
} from "./cells/TransactionConfigCells";
type Row = Transaction;
type TxExtras = TransactionActionsContextValue | null | undefined;

// ─── Sub-query defs (shared by field bodies below) ──────────────────────────
//
// Columns reuse the sub-resource presentations (see
// transactionSubresourceSchemas.ts) — no hand-written select strings.

export const FILES_SUB_QUERY = {
  key: "files",
  resource: "transaction_files",
  foreignKey: "transaction_id" as const,
  columns: [
    toListColumn(transactionFilePresentation.document_type),
    toListColumn(transactionFilePresentation.document_status),
    toListColumn(transactionFilePresentation.review_status),
    toListColumn(transactionFilePresentation.drive_file_id),
    toListColumn(transactionFilePresentation.file_name),
  ],
  filters: [{ field: "is_latest", operator: "eq" as const, value: true }],
} satisfies import("@ds/data/subQuery").SubQueryDef<TransactionFile>;

const EMAIL_SUB_QUERY = {
  key: "emails",
  resource: "email_sends",
  foreignKey: "transaction_id" as const,
  columns: [
    toListColumn(emailSendPresentation.email_type),
    toListColumn(emailSendPresentation.sent_at),
  ],
} satisfies import("@ds/data/subQuery").SubQueryDef<EmailSend>;

export const TRANSPORTS_SUB_QUERY = {
  key: "transports",
  resource: "transaction_transports",
  foreignKey: "transaction_id" as const,
  columns: [
    toListColumn(transportPresentation.sequence_number),
    toListColumn(transportPresentation.transport_type),
    toListColumn(transportPresentation.driver_name),
    toListColumn(transportPresentation.driver_phone),
    toListColumn(transportPresentation.vehicle_plate),
    toListColumn(transportPresentation.container_number),
    toListColumn(transportPresentation.transit),
    toListColumn(transportPresentation.price),
    toListColumn(transportPresentation.price_currency),
    toListColumn(transportPresentation.price_type),
    toListColumn(transportPresentation.invoiced_status),
    toListColumn(transportPresentation.company),
    toListColumn(transportPresentation.commercial_company),
  ],
  filters: [{ field: "is_deleted", operator: "eq" as const, value: false }],
  sorters: [{ field: "sequence_number", order: "asc" as const }],
} satisfies import("@ds/data/subQuery").SubQueryDef<Transport>;

// ─── Checklist helpers ───────────────────────────────────────────────────────

type CheckField = Extract<keyof Row, `${string}_check`>;
function getCheck(r: Row | undefined, key: string): string | null {
  if (!r) return null;
  return (r as unknown as Record<string, string | null>)[`${key}_check`] ?? null;
}

// ─── Reminder email types ────────────────────────────────────────────────────

const REMINDER_EMAIL_TYPES = ["documents_reminder", "documents_scan_reminder"] as const;

// ─── Rule FK — render escape (not on Transaction type; kept standalone) ───────

export const transactionRuleId: ListColumnSpec<Row, EntityLinkProps> = {
  key: "rule_id",
  labelKey: "transactions.fields.rule",
  width: 160,
  defaultVisible: false,
  deps: { fields: ["rule_id"] },
  render: (r) =>
    (r as unknown as { rule?: { id: number; name: string } }).rule
      ? React.createElement(EntityLink, {
          type: "transaction",
          id: (r as unknown as { rule: { id: number; name: string } }).rule.id,
          label: (r as unknown as { rule: { id: number; name: string } }).rule.name,
          variant: "inline",
        })
      : React.createElement(EmptyValue),
};

// ─── View-only record-section fields (subQuery render escapes) ───────────────
// These cannot be expressed as PresentationFields because they use `subQuery` +
// `render` (not a simple component+data+column triple). Declared here as
// SchemaField-shaped constants and wired into the view section layout.fields.
//
// (The per-side faktury invoice fields were removed — the Faktury section is now
// a `kind:"data"` section over invoice_resolved_links, see TransactionFaktury.)

const notifications_unload_confirmation = {
  key: "unload_confirmation",
  labelKey: "transactions.fields.unload_confirmation",
  subQuery: EMAIL_SUB_QUERY,
  deps: { fields: ["unloading_confirmation_sent_check"] },
  render: (r: Row, ctx: RenderContext | undefined) => {
    const sub = ctx?.sub;
    const all = sub ? sub<EmailSend>("emails") : [];
    const emails = all.filter((e) => e.email_type === "unloading_confirmation");
    return (
      <UnloadConfirmationInlineCell
        emails={emails}
        isHidden={getCheck(r, "unloading_confirmation_sent") === "not_required"}
        transactionId={r.id}
        extras={ctx?.extras as TxExtras}
      />
    );
  },
  readOnly: true,
};

const notifications_logistics_emails = {
  key: "logistics_emails",
  labelKey: "transactions.fields.logistics_emails",
  subQuery: EMAIL_SUB_QUERY,
  deps: {
    fields: LOGISTICS_EMAIL_DEFS.map((d) => d.checkKey) as ReadonlyArray<CheckField>,
  },
  render: (r: Row, ctx: RenderContext | undefined) => {
    const sub = ctx?.sub;
    const all = sub ? sub<EmailSend>("emails") : [];
    const row = (r ?? {}) as unknown as Record<string, string | null>;
    const defs = LOGISTICS_EMAIL_DEFS.map((d) => ({
      type: d.type,
      label: d.label,
      checkStatus: row[d.checkKey] ?? null,
      canResend: d.canResend,
    }));
    return (
      <LogisticsEmailsInlineCell
        emails={all}
        defs={defs}
        transactionId={r.id}
        extras={ctx?.extras as TxExtras}
      />
    );
  },
  readOnly: true,
};

const notifications_last_reminder_sent = {
  key: "last_reminder_sent",
  labelKey: "transactions.fields.last_reminder_sent",
  subQuery: EMAIL_SUB_QUERY,
  render: (r: Row, ctx: RenderContext | undefined) => {
    const sub = ctx?.sub;
    const all = sub ? sub<EmailSend>("emails") : [];
    const reminders = all
      .filter((e): e is EmailSend & { sent_at: string } =>
        (REMINDER_EMAIL_TYPES as ReadonlyArray<string>).includes(e.email_type) &&
        !!e.sent_at)
      .sort((a, b) => new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime());
    return <LastReminderInlineCell reminders={reminders} transactionId={r.id} extras={ctx?.extras as TxExtras} />;
  },
  readOnly: true,
};

const notifications_send_reminder = {
  key: "send_reminder",
  labelKey: "transactions.fields.send_reminder",
  subQuery: EMAIL_SUB_QUERY,
  render: (r: Row, ctx: RenderContext | undefined) => {
    const sub = ctx?.sub;
    const all = sub ? sub<EmailSend>("emails") : [];
    const reminders = all.filter((e) =>
      (REMINDER_EMAIL_TYPES as ReadonlyArray<string>).includes(e.email_type));
    return (
      <SendReminderInlineCell
        reminders={reminders}
        transactionId={r.id}
        extras={ctx?.extras as TxExtras}
      />
    );
  },
  readOnly: true,
};

// ─── Display helpers for presentation field overrides ────────────────────────

function fmtWasteCode(code: string | null | undefined): string | undefined {
  if (!code) return undefined;
  const cleaned = code.replace(/\s/g, "");
  if (cleaned.length === 6) return `${cleaned.slice(0, 2)} ${cleaned.slice(2, 4)} ${cleaned.slice(4, 6)}`;
  return code;
}

// ─── 1. Presentation — all field overrides ────────────────────────────────────
//
// Generic columns (auto-derived, no override needed):
//   unload_date, pickup_date_to, reload_date,
//   border_type, gd_status, plus all hidden scalar/flag/check columns.
//
// Flag-only overrides (merge onto auto column):
//   external_id (copyable + edit), order_number (edit).
//
// Full overrides (component + data): everything else below.

export const transactionPresentation = definePresentation("transactions", {
  // ── Flag-only merges ──────────────────────────────────────────────────────
  external_id: {
    copyable: true,
    edit: { key: "external_id", field: { type: "text", maxLength: 80 } },
  },
  order_number: {
    edit: { key: "order_number", field: { type: "text", maxLength: 80, placeholder: "BKG-2026-047" } },
  },

  // ── KV-section presentation field overrides ───────────────────────────────
  // waste_code — reads off product embed (product_id embeds waste_code via productChip.cols)
  waste_code: {
    column: db.transactions.product_id,
    cols: [db.transactions.product_id],
    component: WasteCodeCell,
    data: (r) => ({ value: fmtWasteCode((r as unknown as Row).product?.waste_code) }),
    labelKey: "transactions.fields.waste_code",
    edit: { key: "waste_code", readOnly: true },
    filterable: false, // duplicate of the product filter; waste codes via product_waste_codes supplement
  },
  // pickup_date_from — renders from→to range using both date columns
  pickup_date_from: {
    column: db.transactions.pickup_date_from,
    cols: [db.transactions.pickup_date_to],
    component: PickupDateRangeCell,
    data: (r) => ({ from: r.pickup_date_from, to: r.pickup_date_to }),
    edit: { key: "pickup_date_from", field: { type: "date" } },
    filterable: false,
  },
  // transit — self-wired cell (runtime-loaded EU country options via useCountries)
  transit: {
    column: db.transactions.transit,
    component: TransitEditCell,
    data: (r) => ({ tx: r as unknown as Transaction }),
    edit: { key: "transit", readOnly: true },
    filterable: false,
  },

  // ── Full overrides — identity ─────────────────────────────────────────────
  reference_number: {
    column: db.transactions.reference_number,
    cols: [db.transactions.id, db.transactions.status_override, db.transactions.external_id],
    component: EntityLink,
    props: { type: "transaction", variant: "inline" } as Partial<EntityLinkProps>,
    data: (r) => ({ id: r.id, label: r.reference_number ?? `#${r.id}` }),
    sortable: true,
    edit: { key: "reference_number", readOnly: true },
    filterable: false,
  },

  // ── Dates / times ─────────────────────────────────────────────────────────
  load_date: {
    column: db.transactions.load_date,
    cols: [db.transactions.pickup_date_from],
    component: DateValue,
    data: (r) => ({ value: r.load_date ?? r.pickup_date_from ?? null }),
    sortable: true,
    edit: { key: "load_date", field: { type: "date" } },
  },
  load_time: {
    column: db.transactions.load_time,
    component: TextValue,
    data: (r) => ({ value: r.load_time ? (r.load_time as string).slice(0, 5) : null }),
    edit: { key: "load_time", field: { type: "time" } },
    filterable: false, // time-of-day is not a useful list filter
  },
  unload_time: {
    column: db.transactions.unload_time,
    component: TextValue,
    data: (r) => ({ value: r.unload_time ? (r.unload_time as string).slice(0, 5) : null }),
    edit: { key: "unload_time", field: { type: "time" } },
    filterable: false,
  },

  // ── Weight / quantity ─────────────────────────────────────────────────────
  loaded_weight: {
    column: db.transactions.loaded_weight,
    component: WeightValue,
    data: (r) => ({ value: r.loaded_weight ?? null }),
    align: "right",
    edit: { key: "loaded_weight", readOnly: true },
    filterable: false, // numeric exact-match text filter is useless (no range)
  },
  unloaded_weight: {
    column: db.transactions.unloaded_weight,
    component: WeightValue,
    data: (r) => ({ value: r.unloaded_weight ?? null }),
    align: "right",
    edit: { key: "unloaded_weight", field: { type: "weight" } },
    filterable: false,
  },
  number_of_bales: {
    column: db.transactions.number_of_bales,
    component: TextValue,
    props: { tabular: true },
    data: (r) => ({ value: r.number_of_bales }),
    align: "right",
    // TextValue is deliberately untagged for export (auto-typing it would turn
    // invoice numbers and NIPs into numbers), so a genuinely numeric column
    // declares its own typing.
    exportAs: { type: "number", value: (r: Row) => r.number_of_bales ?? null, numFmt: "#,##0" },
    edit: { key: "number_of_bales", field: { type: "number", min: 0, precision: 0 } },
    filterable: false,
  },

  // ── Prices ────────────────────────────────────────────────────────────────
  load_price: {
    column: db.transactions.load_price,
    cols: [db.transactions.load_price_currency],
    component: AmountDisplay,
    data: (r) => ({ value: r.load_price, currency: r.load_price_currency }),
    align: "right",
    edit: { key: "load_price", readOnly: true },
    filterable: false, // numeric exact-match text filter is useless (no range)
  },
  unload_price: {
    column: db.transactions.unload_price,
    cols: [db.transactions.unload_price_currency],
    component: AmountDisplay,
    data: (r) => ({ value: r.unload_price, currency: r.unload_price_currency }),
    align: "right",
    edit: { key: "unload_price", readOnly: true },
    filterable: false,
  },

  // ── Statuses ──────────────────────────────────────────────────────────────
  status_override: {
    column: db.transactions.status_override,
    component: TransactionStatusTag,
    data: (r) => ({ value: r.status_override ?? null }),
    edit: { key: "status_override", readOnly: true },
    filterable: false, // covered by the quick `statuses` filter (cancelled)
  },
  load_invoiced_status: {
    column: db.transactions.load_invoiced_status,
    component: InvoicedStatusTag,
    data: (r) => ({ value: r.load_invoiced_status ?? null }),
    edit: { key: "load_invoiced_status", readOnly: true },
    filterable: false, // invoice status covered by the checklist (missing_checks)
  },
  unload_invoiced_status: {
    column: db.transactions.unload_invoiced_status,
    component: InvoicedStatusTag,
    data: (r) => ({ value: r.unload_invoiced_status ?? null }),
    edit: { key: "unload_invoiced_status", readOnly: true },
    filterable: false,
  },
  load_received_invoiced_status: {
    column: db.transactions.load_received_invoiced_status,
    component: InvoicedStatusTag,
    data: (r) => ({ value: r.load_received_invoiced_status ?? null }),
    edit: { key: "load_received_invoiced_status", readOnly: true },
    filterable: false, // raw text filter; niche AP-side status, no translated supplement
  },
  unload_received_invoiced_status: {
    column: db.transactions.unload_received_invoiced_status,
    component: InvoicedStatusTag,
    data: (r) => ({ value: r.unload_received_invoiced_status ?? null }),
    edit: { key: "unload_received_invoiced_status", readOnly: true },
    filterable: false,
  },

  // ── Incoterms ─────────────────────────────────────────────────────────────
  load_price_incoterms: {
    column: db.transactions.load_price_incoterms,
    component: EnumChip,
    props: { kind: "incoterms" } as Partial<EnumChipProps>,
    data: (r) => ({ value: r.load_price_incoterms ?? null }),
    labelKey: "transactions.fields.load_incoterms",
    edit: { key: "load_price_incoterms", readOnly: true },
    filterable: false, // niche; raw text filter, not operationally useful in the list
  },
  unload_price_incoterms: {
    column: db.transactions.unload_price_incoterms,
    component: EnumChip,
    props: { kind: "incoterms" } as Partial<EnumChipProps>,
    data: (r) => ({ value: r.unload_price_incoterms ?? null }),
    labelKey: "transactions.fields.unload_incoterms",
    edit: { key: "unload_price_incoterms", readOnly: true },
    filterable: false,
  },

  // ── DPR ───────────────────────────────────────────────────────────────────
  request_dpr: {
    column: db.transactions.request_dpr,
    component: DprChip,
    props: { size: "sm" } as Partial<DprChipProps>,
    data: (r) => ({ active: r.request_dpr }),
    edit: { key: "request_dpr", field: { type: "boolean" } },
  },

  // ── Priority ──────────────────────────────────────────────────────────────
  // Never a visible list column (hidden via config.list.hide + always fetched
  // via TRANSACTION_DEFAULT_COLUMNS). Read/write surface is the basic-details
  // form; the list-level marker is the orange row tone.
  priority: {
    column: db.transactions.priority,
    component: YesNoChip as React.ComponentType<YesNoChipProps>,
    props: {} as Partial<YesNoChipProps>,
    data: (r) => ({ value: r.priority }),
    edit: { key: "priority", field: { type: "boolean" } },
    filterable: false,
  },

  // ── Recycling process ─────────────────────────────────────────────────────
  recycling_process_code: {
    column: db.transactions.recycling_process_code,
    component: RecyclingProcessChip,
    data: (r) => ({ code: r.recycling_process_code }),
    edit: { key: "recycling_process_code", field: { type: "entity-select", kind: "recyclingProcess" } },
    filterable: false,
  },

  // ── Filter opt-outs (flag-only) — columns that get auto-included but make
  //    poor list filters, or were explicitly removed from the audit list. ─────
  load_price_currency:        { filterable: false },
  unload_price_currency:      { filterable: false },
  load_price_type:            { filterable: false },
  unload_price_type:          { filterable: false },
  load_price_vat_rate_code:   { filterable: false },
  unload_price_vat_rate_code: { filterable: false },
  import_drive_folder_id:     { filterable: false },
  border_type:                { filterable: false },
  created_at:                 { filterable: false },
  updated_at:                 { filterable: false },
  reload_date:                { filterable: false },
  pickup_date_to:             { filterable: false },
  issue_dpr:                  { filterable: false },

  // ── Synthetic composite columns ───────────────────────────────────────────
  route: {
    relations: [
      { fk: db.transactions.load_place_company_id, targetCols: [db.companies.id, db.companies.short_name] },
      { fk: db.transactions.unload_place_company_id, targetCols: [db.companies.id, db.companies.short_name] },
    ],
    component: TransactionRoute,
    data: (r) => ({ from: r.load_place_company, to: r.unload_place_company }),
    labelKey: "transactions.fields.route",
    sortable: false,
    edit: { key: "route", readOnly: true },
  },
  price: {
    cols: [
      db.transactions.load_price, db.transactions.load_price_currency,
      db.transactions.unload_price, db.transactions.unload_price_currency,
    ],
    component: AmountDisplay,
    data: (r) => ({
      value: r.load_price ?? r.unload_price,
      currency: r.load_price != null ? r.load_price_currency : r.unload_price_currency,
    }),
    labelKey: "transactions.fields.price",
    align: "right",
    sortable: false,
    edit: { key: "price", readOnly: true },
  },
  // Money columns are computed in the DB (transactions_with_checks → the shared
  // transaction_financials view) — read them as plain fields, no client calc.
  transport_total: {
    cols: [db.transactions_with_checks.transport_pln],
    component: AmountDisplay,
    data: (r) => ({ value: (r as { transport_pln?: number | null }).transport_pln ?? null, currency: "PLN", signedNeutral: true, strong: true }),
    labelKey: "transactions.fields.transport_total",
    align: "right",
    sortable: true,
    edit: { key: "transport_total", readOnly: true },
  },
  margin_pln: {
    cols: [db.transactions_with_checks.margin_pln],
    component: AmountDisplay,
    data: (r) => ({ value: (r as { margin_pln?: number | null }).margin_pln ?? null, currency: "PLN", signedNeutral: true, strong: true }),
    labelKey: "transactions.fields.margin_pln",
    align: "right",
    sortable: true,
    edit: { key: "margin_pln", readOnly: true },
  },

  // ── Relations via specs ───────────────────────────────────────────────────
  product_id: productChip.on(db.transactions.product_id, { required: true }),
  load_place_company_id: companyParty.on(db.transactions.load_place_company_id, { category: "waste_generator", required: true }),
  unload_place_company_id: companyParty.on(db.transactions.unload_place_company_id, { category: "recycler", required: true }),
  load_commercial_company_id: companyParty.on(db.transactions.load_commercial_company_id, { requireNip: true }),
  unload_commercial_company_id: companyParty.on(db.transactions.unload_commercial_company_id, { requireNip: true }),
  exporter_company_id: companyParty.on(db.transactions.exporter_company_id, { category: "exporter" }),
  importer_company_id: companyParty.on(db.transactions.importer_company_id, { category: "importer" }),
  logistics_employee_id: employeeChip.on(db.transactions.logistics_employee_id, { role: "logistics" }),
  load_sales_employee_id: employeeChip.on(db.transactions.load_sales_employee_id, { role: "sales" }),
  unload_sales_employee_id: employeeChip.on(db.transactions.unload_sales_employee_id, { role: "sales" }),
});

// ─── Meta (shared between schema and assembly) ────────────────────────────────

const TX_META = {
  resource: "transactions_with_checks",
  writeResource: "transactions",
  invalidateResources: ["transactions", "transactions_with_checks"],
  entityType: "transaction" as const,
  softDelete: true as const,
  labels: {
    singular: (t: import("i18next").TFunction) => t("transactions.titles.show", "Transakcja"),
    plural:   (t: import("i18next").TFunction) => t("transactions.titles.list", "Transakcje"),
  },
};

// ─── Data schema — the single Schema<Transaction> for sub-table consumers ─────

export const transactionDataSchema = asDataSchema<Transaction>(transactionPresentation, TX_META);

// ─── EDPR-view variant ────────────────────────────────────────────────────────
//
// `transactions_edpr_view` is a wider view that pre-joins waste_codes for
// EDPR eligibility (`is_edpr_eligible`, `available_edpr_mass`, …). Queries
// that filter on those fields MUST target this resource — `transactions_with_checks`
// doesn't carry them and the request errors with HTTP 42703.
//
// Same presentation schema otherwise — every field on `transactions_with_checks`
// is also on `transactions_edpr_view`, plus the EDPR-specific add-ons.

const TX_EDPR_VIEW_META = {
  ...TX_META,
  resource: "transactions_edpr_view",
  invalidateResources: ["transactions", "transactions_with_checks", "transactions_edpr_view"],
};

export const transactionEdprViewDataSchema = asDataSchema<Transaction>(transactionPresentation, TX_EDPR_VIEW_META);

// ─── 2. List placement — bespoke column spine ─────────────────────────────────

const config = definePresentationConfig("transactions", {
  list: {
    hide: [
      "id", "load_place_company_id", "unload_place_company_id", "product_id",
      "load_commercial_company_id", "unload_commercial_company_id",
      "exporter_company_id", "importer_company_id",
      "logistics_employee_id", "load_sales_employee_id", "unload_sales_employee_id",
      "load_price_type", "unload_price_type", "load_price_list_id", "unload_price_list_id",
      "load_price_currency", "unload_price_currency",
      "load_price_incoterms", "unload_price_incoterms",
      "is_deleted", "is_edpr_eligible",
      "priority",
    ],
    add: [
      { key: "route",           width: 240 },
      { key: "price",           width: 140, align: "right", defaultVisible: false },
      { key: "transport_total", width: 140, align: "right", defaultVisible: false },
      { key: "margin_pln",      width: 140, align: "right", defaultVisible: false },
    ],
    columns: {
      reference_number:                { width: 180, pinned: "left", hideable: false },
      external_id:                     { width: 140, defaultVisible: false },
      load_date:                       { width: 120, sorter: true },
      unload_date:                     { width: 120, sorter: true },
      load_time:                       { width: 90,  defaultVisible: false },
      unload_time:                     { width: 90,  defaultVisible: false },
      pickup_date_from:                { width: 120, sorter: true, defaultVisible: false },
      loaded_weight:                   { width: 130, align: "right" },
      unloaded_weight:                 { width: 130, align: "right" },
      number_of_bales:                 { width: 100, align: "right", defaultVisible: false },
      load_price:                      { width: 140, align: "right" },
      unload_price:                    { width: 140, align: "right" },
      status_override:                 { width: 130, align: "center" },
      border_type:                     { width: 130, align: "center", defaultVisible: false },
      gd_status:                       { width: 90,  align: "center", defaultVisible: false },
      load_invoiced_status:            { width: 140, align: "center", defaultVisible: false },
      unload_invoiced_status:          { width: 140, align: "center", defaultVisible: false },
      load_received_invoiced_status:   { width: 160, align: "center", defaultVisible: false },
      unload_received_invoiced_status: { width: 160, align: "center", defaultVisible: false },
      request_dpr:                     { width: 90,  align: "center", defaultVisible: false },
      transit:                         { width: 160, defaultVisible: false },
      recycling_process_code:          { width: 110, defaultVisible: false },
      order_number:                    { width: 140, defaultVisible: false },
      reload_date:                     { width: 110, sorter: true, defaultVisible: false },
      created_at:                      { sorter: true, defaultVisible: false },
      updated_at:                      { sorter: true, defaultVisible: false },
    },
    order: [
      "reference_number", "external_id",
      // relation columns (from presentation) — placement merged above
      "product_id", "load_place_company_id", "unload_place_company_id",
      "load_commercial_company_id", "unload_commercial_company_id",
      "load_date", "unload_date", "load_time", "unload_time", "pickup_date_from",
      "loaded_weight", "unloaded_weight", "number_of_bales",
      "load_price", "unload_price",
      "status_override", "border_type", "gd_status",
      "load_invoiced_status", "unload_invoiced_status",
      "load_received_invoiced_status", "unload_received_invoiced_status",
      "request_dpr",
      "logistics_employee_id", "load_sales_employee_id", "unload_sales_employee_id",
      "transit", "recycling_process_code", "order_number",
      "exporter_company_id", "importer_company_id",
      "reload_date",
      "route", "price", "transport_total", "margin_pln",
    ],
  },
});

// ─── Bespoke list columns (synthetic / hook-computed) ─────────────────────────
//
// toListColumn converts PresentationField → ListColumnSpec with placement knobs.
// Relations use their FK key in the presentation.

const TRANSACTION_LIST_COLUMNS: Array<ListColumnSpec<Row, unknown>> = [
  toListColumn(transactionPresentation.reference_number,              { width: 180, pinned: "left", hideable: false, sorter: true }),
  toListColumn(transactionPresentation.external_id,                   { width: 140, defaultVisible: false }),
  toListColumn(transactionPresentation.product_id,                    { width: 220 }),
  toListColumn(transactionPresentation.load_place_company_id,         { width: 200 }),
  toListColumn(transactionPresentation.unload_place_company_id,       { width: 200 }),
  toListColumn(transactionPresentation.load_commercial_company_id,    { width: 200, defaultVisible: false }),
  toListColumn(transactionPresentation.unload_commercial_company_id,  { width: 200, defaultVisible: false }),
  toListColumn(transactionPresentation.load_date,                     { width: 120, sorter: true, sortField: "effective_load_date" }),
  toListColumn(transactionPresentation.unload_date,                   { width: 120, sorter: true }),
  toListColumn(transactionPresentation.load_time,                     { width: 90 }),
  toListColumn(transactionPresentation.unload_time,                   { width: 90 }),
  toListColumn(transactionPresentation.pickup_date_from,              { width: 120, sorter: true, defaultVisible: false }),
  toListColumn(transactionPresentation.loaded_weight,                 { width: 130, align: "right" }),
  toListColumn(transactionPresentation.unloaded_weight,               { width: 130, align: "right" }),
  toListColumn(transactionPresentation.number_of_bales,               { width: 100, align: "right" }),
  toListColumn(transactionPresentation.load_price,                    { width: 140, align: "right" }),
  toListColumn(transactionPresentation.unload_price,                  { width: 140, align: "right" }),
  toListColumn(transactionPresentation.status_override,               { width: 130, align: "center" }),
  toListColumn(transactionPresentation.border_type,                   { width: 130, align: "center", defaultVisible: false }),
  toListColumn(transactionPresentation.gd_status,                     { width: 90,  align: "center", defaultVisible: false }),
  toListColumn(transactionPresentation.load_invoiced_status,          { width: 140, align: "center", defaultVisible: false }),
  toListColumn(transactionPresentation.unload_invoiced_status,        { width: 140, align: "center", defaultVisible: false }),
  toListColumn(transactionPresentation.load_price_incoterms,          { width: 90,  align: "center", defaultVisible: false }),
  toListColumn(transactionPresentation.unload_price_incoterms,        { width: 90,  align: "center", defaultVisible: false }),
  toListColumn(transactionPresentation.request_dpr,                   { width: 90,  align: "center", defaultVisible: false }),
  toListColumn(transactionPresentation.logistics_employee_id,         { width: 160 }),
  toListColumn(transactionPresentation.load_sales_employee_id,        { width: 160, defaultVisible: false }),
  toListColumn(transactionPresentation.unload_sales_employee_id,      { width: 160, defaultVisible: false }),
  toListColumn(transactionPresentation.transit,                       { width: 160, defaultVisible: false }),
  toListColumn(transactionPresentation.recycling_process_code,        { width: 110, defaultVisible: false }),
  toListColumn(transactionPresentation.order_number,                  { width: 140, defaultVisible: false }),
  toListColumn(transactionPresentation.exporter_company_id,           { width: 200, defaultVisible: false }),
  toListColumn(transactionPresentation.importer_company_id,           { width: 200, defaultVisible: false }),
  toListColumn(transactionPresentation.load_received_invoiced_status, { width: 160, align: "center", defaultVisible: false }),
  toListColumn(transactionPresentation.unload_received_invoiced_status, { width: 160, align: "center", defaultVisible: false }),
  toListColumn(transactionPresentation.reload_date,                   { width: 110, sorter: true, defaultVisible: false }),
  transactionRuleId as ListColumnSpec<Row, unknown>,
  toListColumn(transactionPresentation.transport_total,               { width: 140, align: "right", defaultVisible: false }),
  toListColumn(transactionPresentation.margin_pln,                    { width: 140, align: "right", defaultVisible: false }),
];

const TRANSACTION_DEFAULT_COLUMNS = [
  "reference_number", "product_id", "load_date", "unload_date",
  "load_place_company_id", "unload_place_company_id",
  "unloaded_weight", "unload_price", "load_price",
  // Always fetched (even when its column is hidden) so the list can tint
  // cancelled rows red via TransactionsList's rowToneByRecord.
  "status_override",
  // Always fetched so row-tone (orange) + the logistics client-side pin can
  // read record.priority even though there is no visible priority column.
  "priority",
  // Always fetched so the row menu's "Blokuj automatyczne przypomnienia"
  // checkbox can read record.documents_reminder_blocked (T4 — no visible column).
  "documents_reminder_blocked",
];

// ─── Tab keys (URL-stable; mirror legacy values verbatim) ────────────────────

const TAB_PARTIES   = "strony";
const TAB_TRASA     = "trasa";
const TAB_DOCUMENTS = "dokumenty";
const TAB_PRICING   = "pricing";
const TAB_FAKTURY   = "faktury";
const TAB_ACTIVITY  = "aktywnosc";

// Section anchors (`#section-<key>`) are emitted by SectionShell now, so no
// per-section anchor wrapper is needed.


// ─── 3. View chrome ───────────────────────────────────────────────────────────

const transactionSections: Array<RecordSectionSpec | SectionDef<Row>> = [
  {
    kind: "custom",
    key: "parties",
    column: "main",
    tabKey: TAB_PARTIES,
    title: (t) => t("transactions.sections.parties", "Strony"),
    render: (r) => <TransactionParties transaction={r} />,
  },
  {
    kind: "record",
    key: "basic_details",
    column: "main",
    tabKey: TAB_TRASA,
    title: (t) => t("transactions.sections.basic_details", "Podstawowe dane"),
    tabLabel: (t) => t("transactions.tabs.trasa", "Trasa i transport"),
    fieldKeys: [
      "product_id", "waste_code", "external_id", "order_number",
      "recycling_process_code", "border_type", "gd_status",
      "request_dpr", "transit", "unloaded_weight", "number_of_bales",
    ],
  } satisfies RecordSectionSpec,
  {
    kind: "record",
    key: "dates",
    column: "main",
    tabKey: TAB_TRASA,
    title: (t) => t("transactions.sections.dates", "Daty"),
    fieldKeys: [
      "pickup_date_from", "pickup_date_to", "load_date", "load_time",
      "unload_date", "unload_time",
    ],
  } satisfies RecordSectionSpec,
  // Transport — bespoke per-leg cards + totals. Legs come from the shared
  // page-batched `transports` sub-query (read via useSubQuery in the section),
  // so Transport + Pricing + Faktury share ONE transports fetch.
  {
    kind: "custom",
    key: "transport",
    column: "main",
    tabKey: TAB_TRASA,
    title: (t) => t("transactions.sections.transport", "Transport"),
    subQueries: [TRANSPORTS_SUB_QUERY],
    useSectionState: (r: Transaction) => {
      const workflow = useTransactionWorkflowOptional();
      const onFieldEdit = useEntityFieldEdit(getSchemaMeta(transactionConfig.schema), r.id, {
        onSuccess: workflow?.refetchAll,
      });
      const { rows: legs } = useSubQuery<Transport>("transports", r.id);
      const onSetup = workflow?.external.onSetupTransport
        ? () => workflow.external.onSetupTransport?.(r.id)
        : undefined;
      return {
        onSetup,
        hasLegs: legs.length > 0,
        onLogisticsChange: (v: number | null) => onFieldEdit("", "logistics_employee_id", v),
      };
    },
    status: (r: Transaction, s: { onLogisticsChange: (v: number | null) => void | Promise<void> }) => (
      <AssignCell
        name="logistics_employee_id"
        assignee={r.logistics_employee?.id
          ? { id: r.logistics_employee.id, name: r.logistics_employee.full_name }
          : null}
        entitySelectKind="employee"
        entitySelectRole="logistics"
        placeholder="Nieprzypisana"
        onCommit={(next) => s.onLogisticsChange((next ?? null) as number | null)}
      />
    ),
    actions: (_r, s: { onSetup?: () => void; hasLegs: boolean }, t) =>
      s.onSetup ? [{
        id: "setup-transport",
        label: s.hasLegs
          ? t("transactions.transport.edit", "Edytuj transport")
          : t("transactions.transport.add", "Dodaj transport"),
        icon: <TruckOutlined />,
        variant: "text",
        size: "small",
        onClick: (_r2, st: { onSetup?: () => void }) => st.onSetup?.(),
      }] : [],
    render: (r) => <TransactionTransport transaction={r} />,
  },
  transactionDocumentsSection({
    column: "main",
    tabKey: TAB_DOCUMENTS,
  }),
  {
    kind: "custom",
    key: "pricing",
    column: "main",
    tabKey: TAB_PRICING,
    title: (t) => t("transactions.sections.pricing", "Cennik"),
    // Transport legs (for FX/margin display) come from the page-batched
    // sub-query registry — shared with Transport + Faktury, fetched once.
    subQueries: [TRANSPORTS_SUB_QUERY],
    render: (r) => <TransactionPricing transaction={r} />,
  },
  // Faktury — per-side cards (load/unload/transport): invoice number + chips +
  // gross, or an "issue invoice" placeholder. The framework fetches the links
  // from invoice_resolved_links (SELECT derived from
  // transactionResolvedLinkPresentation); the
  // bespoke per-side layout is injected via a `kind:"custom"` data layout. The
  // transport legs (for the per-leg placeholder cards) come from the shared
  // page-batched `transports` sub-query — read via useSubQuery in the section.
  {
    kind: "data",
    key: "faktury",
    column: "main",
    tabKey: TAB_FAKTURY,
    title: (t) => t("transactions.sections.faktury", "Faktury"),
    subQueries: [TRANSPORTS_SUB_QUERY],
    query: (r) => ({
      schema: transactionResolvedLinkDataSchema,
      fields: ["side", "entity_type", "entity_id", "invoice"],
      filter: [
        { field: "transaction_id", operator: "eq", value: r.id },
        { field: "stale",          operator: "eq", value: false },
      ],
    }),
    layout: {
      kind: "custom",
      render: ({ rows, record }) => (
        <TransactionFaktury transaction={record as Transaction} links={rows} />
      ),
    },
  } satisfies DataSection<Transaction, ResolvedLinkWithInvoice>,
  commentsSection<Transaction>("transactions", {
    key: "comments",
    column: "main",
    tabKey: TAB_ACTIVITY,
    tabLabel: (t) => t("transactions.tabs.activity", "Aktywność"),
    title: (t) => t("transactions.sections.comments", "Komentarze"),
  }),
  activitySection<Transaction>("transaction", {
    key: "activity",
    column: "main",
    tabKey: TAB_ACTIVITY,
    title: (t) => t("transactions.sections.activity", "Aktywność"),
    importantFields: [
      "reference_number", "load_date", "unload_date",
      "loaded_weight", "unloaded_weight", "number_of_bales",
      "product_id", "load_place_company_id", "unload_place_company_id",
      "load_price", "unload_price", "gd_status", "is_deleted",
    ],
    childScopes: [
      // Document verdicts belong in the transaction's history: "who approved
      // which document" (#271). Scoped to `review_status` ONLY — uploads,
      // deletes and classification edits stay audited in the DB but would
      // drown the feed, so they never reach it (not even behind "show all").
      defineAuditChildScope<TransactionFile>({
        resource:   "transaction_files",
        fk:         "transaction_id",
        entityType: "transaction_file",
        select:     "id,document_type,document_status,file_name",
        fields:     ["review_status"],
        label: (row, child, t) => {
          const verdict = (row.after_value?.review_status as string | undefined) ?? "";
          // The file row is gone (hard-deleted) → fall back to its id.
          const document = child
            ? `${t(`documents.document_type.${child.document_type}`)} — ${t(`documents.document_status.${child.document_status}`)}`
            : `#${row.entity_id}`;
          const key = verdict === "approved"
            ? "transactions.activity.document_approved"
            : verdict === "not_approved"
              ? "transactions.activity.document_rejected"
              : "transactions.activity.document_review_changed";
          return t(key, { document });
        },
      }),
      // Transport legs. Legs are hard-created and hard-deleted by
      // TransportSetupModal's per-leg pipeline, so "leg added" / "leg removed"
      // exist only as INSERT / DELETE rows → `lifecycle`. `invoiced_status` is
      // recomputed by DB triggers and `price_list_id` follows re-matching:
      // both are bookkeeping, not operator actions, so neither is scoped in.
      defineAuditChildScope<Transport>({
        resource:   "transaction_transports",
        fk:         "transaction_id",
        entityType: "transaction_transport",
        select:     "id,sequence_number,vehicle_plate,transport_type",
        fields: [
          "transport_company_id", "transport_type", "driver_name", "driver_phone",
          "vehicle_plate", "container_number", "transit",
          "price", "price_currency", "price_type",
        ],
        lifecycle: true,
        label: (row, child, t) => {
          // Leg identity: the live child record, else the INSERT/DELETE
          // snapshot, else the bare id (row gone AND not a snapshot action).
          const snapshot = (row.after_value ?? row.before_value ?? {}) as {
            sequence_number?: number; vehicle_plate?: string | null;
          };
          const seq = child?.sequence_number ?? snapshot.sequence_number;
          const plate = child?.vehicle_plate ?? snapshot.vehicle_plate;
          const leg = seq != null
            ? t("transactions.activity.transport_leg", { seq })
            : `#${row.entity_id}`;
          const label = plate ? `${leg} (${plate})` : leg;
          if (row.action === "INSERT") return t("transactions.activity.transport_added", { leg: label });
          if (row.action === "DELETE") return t("transactions.activity.transport_removed", { leg: label });
          const fields = (row.changed_fields ?? [])
            .map((f) => t(`audit.column_names.transaction_transport.${f}`, { defaultValue: f }))
            .join(", ");
          return t("transactions.activity.transport_changed", { leg: label, fields });
        },
      }),
    ],
  }),
  {
    kind: "custom",
    key: "checklist",
    column: "side",
    render: (r) => <TransactionChecklist transaction={r} />,
  },
  // Notifications — kv-grid over the 4 email fields (subQuery render escapes).
  {
    kind: "record",
    key: "notifications",
    column: "side",
    title: (t) => t("transactions.sections.emails", "Emaile"),
    // Field-level `subQuery: EMAIL_SUB_QUERY` declarations don't auto-lift
    // to the page-level batched fetch; declare the dependency here so the
    // EntityViewPage SubQueriesProvider includes `emails` on first mount.
    subQueries: [EMAIL_SUB_QUERY],
    layout: {
      kind: "kv-grid",
      fields: [
        notifications_unload_confirmation,
        notifications_logistics_emails,
        notifications_last_reminder_sent,
        notifications_send_reminder,
      ],
    },
  },
  {
    kind: "custom",
    key: "meta",
    column: "side",
    bare: true,
    render: (r) => <TransactionMeta transaction={r} />,
  },
];

const viewChrome: ViewChrome<Row> = {
  bodyTemplate: "main-side",
  title: (r) => r.reference_number ?? `#${r.id}`,
  subtitle: (r) => {
    const out: Array<{ icon: "cube" | "shop" | "arrowRight"; children: string }> = [];
    if (r.product?.name) out.push({ icon: "cube", children: r.product.name });
    if (r.load_place_company?.short_name) {
      out.push({ icon: "shop", children: r.load_place_company.short_name });
    }
    if (r.unload_place_company?.short_name) {
      out.push({ icon: "arrowRight", children: r.unload_place_company.short_name });
    }
    return out;
  },
  statusPills: (r) => <TransactionStatusPills transaction={r} />,
  statBand:    (r) => <TransactionKpiBand    transaction={r} />,
  sections:    transactionSections as ViewChrome<Row>["sections"],
};

// ─── 4. Assembly ──────────────────────────────────────────────────────────────

const base = buildEntityConfig<Row>(
  transactionPresentation,
  config,
  {
    ...TX_META,
    title: (r) => r.reference_number ?? `#${r.id}`,
  },
  {
    searchEntity: "transactions",
    title: (t) => t("transactions.titles.list", "Transakcje"),
    defaultSort: [
      { field: "unload_date",      order: "desc" },
      { field: "reference_number", order: "desc" },
    ],
    quickFilters:    PIPELINE_QUICK_FILTERS,
    advancedFilters: PIPELINE_ADVANCED_FILTERS,
    splitPaneKey:    "split-pane:transactions",
    permanentFields: TRANSACTION_DEFAULT_COLUMNS as unknown as string[],
  },
  viewChrome,
);

// Override the generated list spine with the bespoke presentation-derived column
// array (synthetic side-merged cells: route, price, transport_total, margin_pln).
const baseListLayout = base.list.layout;
const list: ListConfig<Row> = {
  ...base.list,
  layout: {
    ...(baseListLayout.kind === "table" ? baseListLayout : { kind: "table" as const }),
    columns: TRANSACTION_LIST_COLUMNS as unknown as ListColumnSpec<Row>[],
  },
};

export const transactionConfig: EntityConfig<Row> = {
  ...base,
  list,
};
